"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.argonBenchmark = exports.customNow = void 0;
const hash_wasm_1 = require("hash-wasm");
const util_1 = require("./util");
function argonBenchInner(memorySize, iterations, now) {
    return __awaiter(this, void 0, void 0, function* () {
        const password = yield (yield util_1.crypto).getRandomValues(new Uint8Array(32));
        const salt = yield (yield util_1.crypto).getRandomValues(new Uint8Array(32));
        const start = now();
        yield hash_wasm_1.argon2id({
            password,
            salt,
            parallelism: 1,
            memorySize,
            iterations,
            hashLength: 32,
            outputType: "binary",
        });
        return now() - start;
    });
}
function customNow(now, roundMs, jitter) {
    if (jitter) {
        let lastRealNow = 0;
        let lastNow = 0;
        now = ((thisNow) => (() => {
            const realNow = thisNow();
            const nextNow = lastRealNow === 0 ? realNow : (() => {
                const elapsed = (realNow - lastRealNow) * (1 + ((Math.random() - 0.5) * jitter));
                return lastRealNow + elapsed;
            })();
            lastRealNow = realNow;
            lastNow = nextNow;
            return nextNow;
        }))(now);
    }
    if (roundMs) {
        now = ((thisNow) => (() => Math.round(thisNow() / roundMs) * roundMs))(now);
    }
    return now;
}
exports.customNow = customNow;
/**
 * This function benchmarks the current machine to determine how many iterations of single-theaded argon2id can be afforded in a given time budget.
 * The time budget is considered to include only the time spent actually iterating; setup time is proportional to the memory allocation and excluded,
 * though it usually only is a few tens of milliseconds extra.
 *
 *  Benchmarks in Node and the browser show that the error in the final value is typically less than 5%, with
 * @param memorySizeKib The amount of memory used by argon2id; must be at least 8. A higher value provides more defense against ASIC and GPU attacks
 * @param targetTimeMs Desired duration of the key derivation operation
 * @param options Optional parameters primarily useful for testing the benchmark implementation
 * @param options.fineTuning Number of fine-tuning rounds permitted; defaults to 0. Allowing more fine-tuning rounds reduces error.
 * @param options.measureError Calculate the error of the final result by measuring the precise time the recommended number of iterations takes.
 * @param options.now Overrides the timestamp function used to measure durations. Must return a monotonically-increasing number of milliseconds.
 * @param options.roundMs Rounds timestamps to simulate results when a browser rounds performance.now() to prevent fingerprinting.
 * @param options.jitter Introduces random timestamp errors of the specified magnitude. Range 0-1, default 0.
 * @returns
 */
function argonBenchmark(memorySizeKib, targetTimeMs, options = {}) {
    var _a, _b, _c, _d, _e;
    return __awaiter(this, void 0, void 0, function* () {
        const preciseNow = (_a = options.now) !== null && _a !== void 0 ? _a : (yield util_1.performance).now.bind(yield util_1.performance);
        const overallStart = preciseNow();
        let fineTuning = (_b = options.fineTuning) !== null && _b !== void 0 ? _b : 0;
        const measureError = (_c = options.measureError) !== null && _c !== void 0 ? _c : false;
        const roundedNow = customNow(preciseNow, (_d = options.roundMs) !== null && _d !== void 0 ? _d : 0, (_e = options.jitter) !== null && _e !== void 0 ? _e : 0);
        // warm-up wasm module with the minimum-possible parameter values to ensure the module is actually loaded; should take
        // no more than 100ms in the absolute worst case
        const warmupDuration = yield argonBenchInner(8, 1, roundedNow);
        // Calculate the minimum time a single iteration of the shortest kind takes; this is later used to calculate the bits
        // of added security. The low memory use ensures that the setup time is negligible.
        const minMsPerIter = yield (() => __awaiter(this, void 0, void 0, function* () {
            let minDuration = 0;
            let minMsPerIter = 0;
            for (let i = 1; minDuration === 0; i *= 2) {
                minDuration = yield argonBenchInner(8, 1, roundedNow);
                minMsPerIter = minDuration / i;
            }
            return minMsPerIter;
        }))();
        let firstDuration = 0;
        let firstI = 0;
        let duration = 0;
        let msPerIteration = 0;
        let setupDuration = 0;
        let i = 0.5; // after the initial i *= 2, this will always be an integer
        while (duration < (targetTimeMs + setupDuration)) {
            if (msPerIteration === 0) {
                // This ensures that a non-increasing performance.now() implementation won't loop infinitely
                if (i * memorySizeKib > 2 ** 20)
                    break;
                // This happen on the first loop, but also can happen with rounded timestamps
                i *= 2;
            }
            else {
                const iStep = Math.round(((targetTimeMs + setupDuration) - duration) / msPerIteration);
                // We don't need fine-tuning if we're dead on
                if (iStep == 0)
                    break;
                // Ensuring each step is at least i large sacrifices accuracy, but limits the benchmark time to no more than 4
                // times the target duration. Additional fine-tuning steps can increase accuracy, but each one adds a possible
                // 2 * targetTimeMs to the worst-case.
                if (Math.abs(iStep) < 0) {
                    if (fineTuning <= 0)
                        break;
                    fineTuning--;
                }
                i = i + iStep;
            }
            duration = yield argonBenchInner(memorySizeKib, i, roundedNow);
            msPerIteration = (duration - setupDuration) / i;
            // We want the the duration of the first *measurable* run (i.e. duration > 0). (Duration can be 0 if timestamps are
            // rounded.)
            if (!firstDuration) {
                firstDuration = duration;
                firstI = i;
            }
            // As we test higher iteration counts, we get a more accurate msPerIteration calculation, leading to 
            setupDuration = Math.max(0, firstDuration - (msPerIteration * firstI));
        }
        if (msPerIteration === 0)
            throw new Error("benchmark runs look instantaneous -- is performance.now() working?");
        // Allow no more than i/2 to be shaved off the final time; this limits the damage a significantly outlying final
        // benchmark run can do 
        i -= Math.min((duration - (targetTimeMs + setupDuration)) / msPerIteration, i / 2);
        i = Math.ceil(i);
        let error = undefined;
        if (measureError) {
            // bench with recommended iterations to determine precise error; this will obviously take extra time
            const finalDuration = yield argonBenchInner(memorySizeKib, i, preciseNow);
            error = Math.abs(finalDuration - (targetTimeMs + setupDuration)) / (targetTimeMs + setupDuration);
        }
        const bits = Math.log2(((i * msPerIteration) - setupDuration) / minMsPerIter);
        const overallDuration = preciseNow() - overallStart;
        return {
            iterations: i,
            bits,
            error,
            durations: {
                warmup: warmupDuration,
                setup: setupDuration,
                overall: overallDuration,
                msPerIteration,
            },
            options: Object.assign(Object.assign({}, Object.assign(Object.assign({}, options), { now: undefined })), { memorySizeKib,
                targetTimeMs })
        };
    });
}
exports.argonBenchmark = argonBenchmark;
